import axios from 'axios';
import {
  API_URL, setToken, setUser, clearToken, getToken,
  setOtpSession, getOtpTrackingId, clearOtpSession,
  getRefreshToken, setRefreshToken, clearRefreshToken,
} from '../variables/constants';
import { renouvelerSilencieusement } from './renouvellementSession';
import { entetesAppareil } from '../utils/infoAppareil';
import { viderNavigation } from './navigationService';
import stompClient from './stompClient';
import i18n from '../i18n';

/**
 * Options communes aux appels d'authentification.
 *
 * `withCredentials` est indispensable : sans lui, le navigateur n'envoie pas le
 * cookie `cb_did` au serveur d'API (origine distincte du front), l'appareil est
 * inconnu à chaque connexion, et un code serait redemandé indéfiniment même sur
 * un poste déjà mis en confiance.
 */
const optionsAuth = (extra = {}) => ({
  withCredentials: true,
  headers: { ...entetesAppareil(), ...(extra.headers || {}) },
  ...extra,
});

/**
 * Options des appels au compte déjà connecté (`/v1/compte/**`).
 *
 * `optionsAuth` sert les routes de `/v1/auth`, celles qu'on atteint AVANT d'être
 * authentifié : elle ne porte donc aucun jeton, et `JwtAuthFilter.shouldNotFilter`
 * ignore de toute façon ce préfixe. Les routes `/v1/compte` demandent l'exact
 * inverse - une session ouverte, que le filtre lit dans l'en-tête `Authorization`
 * et NULLE PART ailleurs : il n'y a aucun repli sur un cookie, `withCredentials`
 * ne supplée donc rien. Les servir avec `optionsAuth` les faisait toutes refuser,
 * et l'écran n'en voyait qu'un appel échoué : « État indisponible ».
 *
 * L'en-tête d'appareil est conservé ici, et non écrasé : `optionsAuth({ headers })`
 * ne l'aurait pas fait, son `...extra` final remplaçant l'objet `headers` déjà
 * fusionné.
 */
const optionsCompte = () => ({
  withCredentials: true,
  headers: {
    ...entetesAppareil(),
    Authorization: `Bearer ${getToken() || ''}`,
  },
});

/**
 * Le choix « Se souvenir de moi » doit traverser l'étape du code de connexion :
 * il est fait à l'écran de connexion, les jetons n'arrivent qu'après l'OTP.
 * En sessionStorage - le choix ne concerne que cette tentative de connexion.
 */
const CHOIX_SOUVENIR_KEY = 'cb_souvenir_demande';
const memoriserChoixSeSouvenir = (v) => {
  try { sessionStorage.setItem(CHOIX_SOUVENIR_KEY, v ? '1' : '0'); } catch { /* stockage indisponible */ }
};
const relireChoixSeSouvenir = () => {
  try { return sessionStorage.getItem(CHOIX_SOUVENIR_KEY) === '1'; } catch { return false; }
};

const extractTokenAndStore = (data, seSouvenir = false) => {
  const accessToken = data?.tokens?.access_token ?? data?.access_token ?? data?.token;
  if (!accessToken) throw new Error(i18n.t('referentiels:auth.tokenAbsent'));
  setToken(accessToken);

  //  Chercher l'user dans data.user OU directement dans data
  const user = data?.user ?? data?.utilisateur ?? data;
  // L'UUID n'a pas la meme cle selon la source :
  //   /v1/auth/login   -> UserInfoResponse.trackingId
  //   /v1/utilisateurs -> UtilisateurResponse.utilisateurTrackingId
  // On normalise sur `utilisateurTrackingId`, seule cle lue par le reste du front.
  const trackingId = user?.utilisateurTrackingId ?? user?.trackingId;
  const utilisateurNormalise = trackingId ? { ...user, utilisateurTrackingId: trackingId } : null;
  if (utilisateurNormalise) {
    setUser(utilisateurNormalise);
  }

  // Le serveur émet toujours un refresh token pour le canal web ; on ne le
  // conserve que si l'utilisateur a demandé qu'on se souvienne de lui. Sinon la
  // session meurt avec l'onglet, exactement comme avant.
  const refreshToken = data?.tokens?.refresh_token ?? data?.refresh_token;
  if (seSouvenir && refreshToken) setRefreshToken(refreshToken, utilisateurNormalise);
  else clearRefreshToken();

  if (!getToken()) throw new Error(i18n.t('referentiels:auth.stockageTokenEchoue'));
};

export const authService = {
  /**
   * @param {boolean} seSouvenir case « Se souvenir de moi ». Elle était affichée
   *   mais sa valeur n'était jamais lue : la session mourait avec l'onglet quoi
   *   qu'on coche.
   */
  login: async (login, password, seSouvenir = false) => {
    const res = await axios.post(`${API_URL}/v1/auth/login`, {
      login, password, isMobile: false,
    }, optionsAuth());
    const data = res.data?.data ?? res.data;

    // Appareil bloqué ou demande de validation en cours : aucun jeton n'est émis
    // et il n'y a pas de code à saisir. On rend l'issue telle quelle, l'écran de
    // connexion l'affiche.
    if (data?.statut === 'APPAREIL_BLOQUE' || data?.statut === 'VALIDATION_ADMIN_EN_COURS') {
      clearOtpSession();
      return { statut: data.statut, message: data.message };
    }

    if (data?.requires2FA) {
      setOtpSession(data.otpTrackingId, data.maskedEmail, data);
      // Le choix est fait sur l'écran de connexion, mais les jetons ne sont émis
      // qu'après le code : sans ce relais, cocher la case n'aurait aucun effet
      // pour les comptes en double authentification - c'est-à-dire la plupart.
      memoriserChoixSeSouvenir(seSouvenir);
      return { requires2FA: true, maskedEmail: data.maskedEmail };
    }

    extractTokenAndStore(data, seSouvenir);
    return data;
  },

  validateActivation: async (token) => {
    const res = await axios.get(`${API_URL}/v1/auth/activation/${token}`);
    return res.data?.data ?? res.data;
  },

  activateAccount: async ({ token, password }) => {
    const res = await axios.post(`${API_URL}/v1/auth/activation`, { token, password });
    return res.data?.data ?? res.data;
  },

  /**
   * @param {string} code
   * @param {boolean} faireConfiance case « Faire confiance à cet appareil ».
   *   Jamais cochée par défaut : c'est à l'agent de décider d'allonger la durée
   *   de vie de cet accès, pas à l'écran de le décider pour lui.
   */
  /**
   * @param methode voie empruntée : 'EMAIL' (le code reçu) ou 'AUTHENTICATOR'
   *                (le code affiché par l'application, quand le courriel n'arrive pas).
   * @returns les données de session, avec éventuellement `enrolementPropose`
   *          (QR + clé) quand le compte pourrait associer une application.
   */
  verifyOtp: async (code, faireConfiance = false, methode = 'EMAIL') => {
    const otpTrackingId = getOtpTrackingId();
    if (!otpTrackingId) throw new Error(i18n.t('referentiels:auth.sessionOtpIntrouvable'));
    const res = await axios.post(`${API_URL}/v1/auth/verify-otp`,
      { otpTrackingId, code, faireConfiance, methode }, optionsAuth());
    const data = res.data?.data ?? res.data;
    extractTokenAndStore(data, relireChoixSeSouvenir());
    clearOtpSession();
    return data;
  },

  /** Etat de l'application d'authentification : disponible, autorisee, associee. */
  etatAuthenticator: async () => {
    const res = await axios.get(`${API_URL}/v1/compte/authenticator/etat`, optionsCompte());
    return res.data;
  },

  /**
   * Démarre une association depuis le profil.
   *
   * Le mot de passe est exigé par le serveur : l'association n'était proposée
   * qu'après une preuve fraîche du second facteur, et sans lui une session volée
   * pourrait associer le téléphone d'un tiers.
   */
  demarrerAuthenticator: async (motDePasse) => {
    const res = await axios.post(
      `${API_URL}/v1/compte/authenticator/demarrer`, { motDePasse }, optionsCompte());
    return res.data;
  },

  confirmerAuthenticator: async (code) => {
    const res = await axios.post(
      `${API_URL}/v1/compte/authenticator/confirmer`, { code }, optionsCompte());
    return res.data?.data ?? res.data;
  },

  retirerAuthenticator: async () => {
    const res = await axios.delete(`${API_URL}/v1/compte/authenticator`, optionsCompte());
    return res.data?.data ?? res.data;
  },

  resendOtp: async () => {
    const otpTrackingId = getOtpTrackingId();
    if (!otpTrackingId) throw new Error(i18n.t('referentiels:auth.sessionOtpIntrouvable'));
    const res = await axios.post(`${API_URL}/v1/auth/resend-otp`, { otpTrackingId }, optionsAuth());
    const data = res.data?.data ?? res.data;
    if (data?.otpTrackingId) {
      setOtpSession(data.otpTrackingId, data.maskedEmail, data);
    }
    return data;
  },

  /** « Je ne reçois pas le code » : transmet la demande aux administrateurs. */
  demanderValidationAppareil: async ({ motif = 'OTP_NON_RECU', commentaire } = {}) => {
    const otpTrackingId = getOtpTrackingId();
    if (!otpTrackingId) throw new Error(i18n.t('referentiels:auth.sessionOtpIntrouvable'));
    const res = await axios.post(`${API_URL}/v1/auth/appareil/demander-validation`,
      { otpTrackingId, motif, commentaire }, optionsAuth());
    clearOtpSession();
    return res.data?.data ?? res.data;
  },

  refreshTokenWithPassword: async (login, password) => {
    const res = await axios.post(`${API_URL}/v1/auth/refresh-token`, {
      login, password,
    }, {
      headers: { Authorization: `Bearer ${getToken() || ''}` },
    });
    const data = res.data?.data ?? res.data;
    // On conserve le choix déjà fait : une re-saisie de mot de passe ne doit pas
    // révoquer un « Se souvenir de moi » que l'utilisateur avait demandé.
    extractTokenAndStore(data, Boolean(getRefreshToken()));
    return data;
  },

  /**
   * Renouvelle l'accès sans rien redemander à l'utilisateur - « Se souvenir de
   * moi ». Réexporté ici par commodité ; l'implémentation vit dans
   * `renouvellementSession` pour rester utilisable depuis l'intercepteur axios
   * sans boucler les imports.
   */
  renouvelerSilencieusement,

  logout: async () => {
    stompClient.arreter();
    try {
      const token = getToken();
      if (token)
        await axios.post(`${API_URL}/v1/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
    } catch (_) { }
    clearToken();
    clearOtpSession();
    // Sans cette purge, l'utilisateur suivant sur le meme onglet heriterait du menu du precedent.
    viderNavigation();
  },

  isAuthenticated: () => !!getToken(),

  /**
   * Réinitialisation en libre-service, étape 1 : demande d'un code.
   * Le serveur rend toujours la même forme de réponse, que l'adresse
   * corresponde à un compte ou non - l'écran ne doit jamais tenter de
   * distinguer les deux cas.
   */
  demanderReinitialisation: async (email) => {
    const res = await axios.post(`${API_URL}/v1/auth/mot-de-passe/oubli`, { email });
    return res.data?.data ?? res.data;
  },

  /** Réinitialisation en libre-service, étape 2 : validation du code et changement du mot de passe. */
  reinitialiserMotDePasse: async (otpTrackingId, code, nouveauMotDePasse) => {
    const res = await axios.post(`${API_URL}/v1/auth/mot-de-passe/reinitialiser`,
      { otpTrackingId, code, nouveauMotDePasse });
    return res.data?.data ?? res.data;
  },
};
